import { useEffect } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAppSelector } from '@store/hooks'
import {
  useGetUnreadCountQuery,
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useRunDigestChecksMutation,
  notificationApi,
  Notification,
} from '@store/api/notificationApi'
import { useAppDispatch } from '@store/hooks'

let socketInstance: Socket | null = null

/**
 * Hook for accessing and managing notifications.
 * Provides real-time updates via WebSocket when user is authenticated.
 */
export const useNotifications = () => {
  const { token, isAuthenticated } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()

  const { data: unreadCount = 0, refetch: refetchCount } = useGetUnreadCountQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 60000, // fallback polling every 60s
  })

  const { data: notificationsData, refetch: refetchNotifications } = useGetNotificationsQuery(
    { page: 1, limit: 20 },
    { skip: !isAuthenticated }
  )

  const [markAsRead] = useMarkAsReadMutation()
  const [markAllAsRead] = useMarkAllAsReadMutation()
  const [deleteNotification] = useDeleteNotificationMutation()
  const [runDigestChecks] = useRunDigestChecksMutation()

  // Session reminders and wishlist availability checks on login
  useEffect(() => {
    if (!isAuthenticated) return
    runDigestChecks()
  }, [isAuthenticated, runDigestChecks])

  // Real-time WebSocket updates
  useEffect(() => {
    if (!isAuthenticated || !token) return

    const userId = localStorage.getItem('userId')
    if (!userId) return

    if (!socketInstance) {
      socketInstance = io('/', { auth: { token } })
    }

    socketInstance.emit('join-user-room', userId)

    socketInstance.on('notification', (_notification: Notification) => {
      // Invalidate RTK Query cache to trigger a refetch
      dispatch(notificationApi.util.invalidateTags(['Notification', 'UnreadCount']))
    })

    return () => {
      if (socketInstance) {
        socketInstance.off('notification')
      }
    }
  }, [isAuthenticated, token, dispatch])

  return {
    unreadCount,
    notifications: notificationsData?.notifications ?? [],
    pagination: notificationsData?.pagination,
    markAsRead: (id: string) => markAsRead(id),
    markAllAsRead: () => markAllAsRead(),
    deleteNotification: (id: string) => deleteNotification(id),
    refetch: () => {
      refetchCount()
      refetchNotifications()
    },
  }
}

export default useNotifications
